import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { FAQS, IMAGES, COMPANY } from "../data/content";

export default function FAQ() {
  return (
    <div>
      <SEO
        title="FAQ | Building with Kotam Projects | VIC & SA Builders"
        description="Answers to common questions about building with Kotam Projects — service areas across Victoria and South Australia, project types, timelines, licensing, safety and how to request a quote."
        path="/faq"
      />
      <section className="relative pt-36 pb-20 sm:pt-44 sm:pb-28 overflow-hidden bg-forest-dark text-white">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.facade} alt="Contemporary home facade built by Kotam Projects" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/80 to-forest-dark/50" />
        <div className="container-px relative">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="label text-gold">
            Frequently Asked Questions
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="mt-6 font-display font-light text-hero max-w-4xl">
            Answers, before you ask.
          </motion.h1>
        </div>
      </section>

      <section className="container-px py-24 sm:py-32 lg:py-40">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="label text-gold">Still curious?</p>
              <h2 className="mt-6 font-display font-light text-h3 text-charcoal">
                We're here to help with anything else.
              </h2>
              <p className="mt-6 text-lg text-charcoal/60 leading-relaxed">
                Can't find what you're looking for? Reach out and our team will get back to you promptly.
              </p>
              <Link to="/contact" data-testid="faq-contact-cta" className="mt-8 group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 font-semibold text-white transition-colors hover:bg-gold hover:text-forest-dark">
                Contact us <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="mt-6 block text-charcoal/50">{COMPANY.phone}</a>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-display text-xl sm:text-2xl font-medium text-charcoal hover:text-forest hover:no-underline [&[data-state=open]]:text-forest">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-lg text-charcoal/60 leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
